import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import type {
  AgentExecutionStatusType,
  Artifact,
  TraceFile,
} from '@terra/shared-schemas-ai';
import type { TraceStatusType } from '@terra/shared-schemas-ai';
import type { AgentExecutionHistoryResponseDto } from './agent-execution-history.schema';
import type { PlanItemStatusType } from './agent-execution.schema';

const STATUS_MAP: Record<AgentExecutionStatusType, TraceStatusType> = {
  PENDING: 'RUNNING',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  ABORTED: 'ABORTED',
  BLOCKED: 'BLOCKED',
};

const PLAN_STATUS_MAP: Record<
  PlanItemStatusType,
  'pending' | 'completed' | 'in_progress' | 'cancelled'
> = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

function buildArtifactLookup(artifacts: Artifact[]): Map<string, Artifact> {
  const byId = new Map<string, Artifact>();
  const byRuntimeId = new Map<string, Artifact>();

  for (const artifact of artifacts) {
    byId.set(artifact.id, artifact);
    if (artifact.runtimeId) {
      byRuntimeId.set(artifact.runtimeId, artifact);
    }
  }

  return new Map([...byRuntimeId, ...byId]);
}

export function adaptAgentExecutionHistory(
  data: AgentExecutionHistoryResponseDto,
): SmokeTestHistoryResponseDto {
  const artifactLookup = buildArtifactLookup(data.artifacts);

  const evidences = (data.evidences ?? []).map((e) => {
    const artifact = artifactLookup.get(e.artifactId);
    return {
      explanation: e.description,
      artifactId: e.artifactId,
      type: artifact?.type,
      toolExecutionHarId: artifact?.toolCallId ?? undefined,
    };
  });

  const metadata =
    data.summary || evidences.length > 0
      ? {
          analysis: data.summary ?? undefined,
          evidences: evidences.length > 0 ? evidences : undefined,
        }
      : undefined;

  const artifactFiles = data.artifacts.flatMap(mapArtifactToTraceFiles);

  return {
    threadId: data.executionId,
    signalId: data.signalId ?? null,
    signal: data.signal ?? null,
    plan: data.plan.map((item) => ({
      id: item.id,
      content: item.description,
      status: PLAN_STATUS_MAP[item.status],
    })),
    messages: data.messages,
    files: [...data.files, ...artifactFiles],
    artifacts: data.artifacts,
    status: STATUS_MAP[data.status],
    metadata,
  };
}

function mapArtifactToTraceFiles(artifact: Artifact): TraceFile[] {
  const files: TraceFile[] = [];

  if (artifact.actionFileName && artifact.actionContent !== null) {
    files.push({
      id: `${artifact.id}:action`,
      tenantId: artifact.tenantId,
      traceId: artifact.traceId ?? '',
      threadId: artifact.threadId ?? '',
      name: artifact.actionFileName,
      content: artifact.actionContent,
      size: artifact.actionContentSize ?? artifact.actionContent.length,
      createdAt: artifact.createdAt,
    });
  }

  if (artifact.resultFileName && artifact.resultContent !== null) {
    files.push({
      id: `${artifact.id}:result`,
      tenantId: artifact.tenantId,
      traceId: artifact.traceId ?? '',
      threadId: artifact.threadId ?? '',
      name: artifact.resultFileName,
      content: artifact.resultContent,
      size: artifact.resultContentSize ?? artifact.resultContent.length,
      createdAt: artifact.createdAt,
    });
  }

  return files;
}
